import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { useState } from "react";
import { useDebounce } from "@reactuses/core";
import { useTranslation } from "react-i18next";
import { GridList, GridListItem } from "react-aria-components";
import { FaXmark } from "react-icons/fa6";
import { css } from "../../../styled-system/css";
import { Flex, HStack, styled, VStack, Wrap } from "../../../styled-system/jsx";
import { flex, grid } from "../../../styled-system/patterns";
import { Button } from "../../components/core/basic/Button";
import { SearchInput } from "../../components/fields/base/TextField";
import { Modal } from "../../components/core/basic/Modal";

interface Props {
  tags: string[];
  selectedTags: string[];
  setSelectedTags: (tags: string[]) => void;

  getIrrelevantTagsHiddenText?: (numOfTags: number) => string;
  sortTags?: (a: string, b: string) => number;
  hideIrrelevantTags?: (tag: string) => boolean;
  getDescription?: (tag: string) => string;
  tagCount: Record<string, number>;
}

function TagSelector({
  tags,
  selectedTags,
  setSelectedTags,
  tagCount,
  getIrrelevantTagsHiddenText,
  sortTags = (a, b) => a.localeCompare(b),
  hideIrrelevantTags = () => false,
}: Props) {
  const { t } = useTranslation(["tags", "common"]);
  const [query, setQuery] = useState("");
  const debounceQuery = useDebounce(query, 500);
  const irrelevantTags = tags.filter(hideIrrelevantTags).map((tag) => tag);
  const renderIrrelevantHiddenText = (count: number) =>
    getIrrelevantTagsHiddenText ?
      getIrrelevantTagsHiddenText(count)
    : t(($) => $.tags["{count} tags not shown"], { count });

  return (
    <VStack gap="medium" alignItems={"stretch"}>
      <Wrap gap="medium">
        <SearchInput
          placeholder={t(($) => $.tags.multiSelect["Search tags..."])}
          value={query}
          onChange={(value) => {
            setQuery(value);
          }}
          onClear={() => {
            setQuery("");
          }}
          css={{ flex: "0 1 450px" }}
        />
        <Button variant="link" onPress={() => setSelectedTags([])} isDisabled={!selectedTags.length}>
          {t(($) => $.common.actions["Clear selection"])}
        </Button>
        {!!selectedTags.length && (
          <span className={flex({ alignItems: "center" })}>
            {t(($) => $.tags["{count} tags selected"], { count: selectedTags.length })}
          </span>
        )}
        {irrelevantTags.length > 0 && (
          <span className={flex({ alignItems: "center", color: "text.secondary" })}>
            {renderIrrelevantHiddenText(irrelevantTags.length)}
          </span>
        )}
      </Wrap>
      {!!selectedTags.length && (
        <Wrap>
          {tags
            .filter((c) => selectedTags.includes(c))
            .map((tag) => (
              <Button variant="link" key={tag} onPress={() => setSelectedTags(selectedTags.filter((c) => c !== tag))}>
                {tag}
                <FaXmark />
              </Button>
            ))}
        </Wrap>
      )}
      <GridList
        aria-label={t(($) => $.tags["Tag filter"])}
        className={grid({
          minChildWidth: "200px",
          maxHeight: "60vh",
          padding: "2px", // So the outline is visible when keyboard navigationg
          scrollbarColor: "{colors.lightgrey} transparent",
          scrollbarWidth: "thin",
          overflowY: "auto",
        })}
        selectionMode={"multiple"}
        layout={"grid"}
        items={tags
          .filter((tag) => tag.toLowerCase().includes(debounceQuery.toLowerCase()) && !irrelevantTags.includes(tag))
          .sort(sortTags)
          .map((tag) => ({ id: tag, label: tag, count: tagCount[tag] || 0 }))}
        selectedKeys={selectedTags}
        onSelectionChange={(selectedKeys) => {
          setSelectedTags(selectedKeys === "all" ? [] : (Array.from(selectedKeys) as string[]));
        }}
      >
        {(item) => {
          return <TagItem key={item.id} tag={item.label} count={item.count} />;
        }}
      </GridList>
    </VStack>
  );
}

const TagCard = styled(GridListItem, {
  base: {
    all: "unset",
    cursor: "pointer",
    display: "flex",
    position: "relative",
    gap: "small",
    flexDirection: "column",
    justifyContent: "space-between",
    border: "1px solid {colors.lightgrey}",
    borderRadius: "standard",
    padding: "{radii.standard}",
    "&[data-selected]": {
      borderColor: "trellisPalette.3",
    },
    "&[data-focus-visible='true']": {
      outline: "2px solid {colors.trellisPalette.3}",
    },
  },
});

function TagItem({ tag, count }: { tag: string; count: number }) {
  return (
    <TagCard id={tag} textValue={tag}>
      <Flex gap="medium">
        <span className={css({ fontSize: "16px", fontWeight: "500" })}>
          {tag} ({count})
        </span>
      </Flex>
    </TagCard>
  );
}

export const TagSelectorModal = NiceModal.create(
  ({
    initialSelectedTags,
    onConfirm,
    ...rest
  }: {
    initialSelectedTags: string[];
    onConfirm: (selectedTags: string[]) => void;
  } & Omit<Props, "selectedTags" | "setSelectedTags">) => {
    const { t } = useTranslation(["tags", "common"]);
    const modal = useModal();
    const [selectedTags, setSelectedTags] = useState(initialSelectedTags);

    return (
      <Modal
        css={{
          minWidth: "80vw",
          maxWidth: "unset",
          maxHeight: "unset",
          padding: "large",
        }}
        isDismissable
        isOpen={modal.visible}
        title={t(($) => $.tags["Choose Tags"])}
        onOpenChange={(isOpen) => !isOpen && modal.remove()}
      >
        <TagSelector selectedTags={selectedTags} setSelectedTags={setSelectedTags} {...rest} />

        <HStack gap="medium" justifyContent={"space-around"}>
          <Button variant="secondary" onPress={() => modal.remove()}>
            {t(($) => $.common.actions.Cancel)}
          </Button>
          <Button
            variant="primary"
            onPress={() => {
              onConfirm(selectedTags);
              modal.remove();
            }}
          >
            {t(($) => $.common.actions.Apply)}
          </Button>
        </HStack>
      </Modal>
    );
  },
);
